/* eslint-disable no-prototype-builtins */
import React, { useEffect, useMemo, useState } from 'react';
import { CheckedList } from 'modules/Modals/old/FilterModal/components/CheckboxGroup';
import { CollapsedTab } from 'modules/Modals/old/FilterModal/components/CollapsedTab';
import ProximityFilterSection from 'modules/Modals/old/FilterModal/components/ProximityFilterSection';
import { NewFiltersEntity, OldFilterEntity, RetailerEntity } from '_entities';
import { HasAccess, IsMdAccount, IsAzAccount } from 'modules';
import { useHiddenFilters, resolveVisitPage } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { BaseSelect, Loader } from 'ui-kit';
import { useLocation } from 'react-router-dom';
import Location from 'modules/Modals/FilterPages/Location';
import { PERMISSIONS } from '_constants';
import { FILTER_FIELDS, FILTER_RETAILERS } from '_constants/old/filters';
import { FormGroup } from 'modules/Modals/old/FilterModal/components/FormGroup';
import RadioButtonsOrganizer from 'ui-kit/old/radioButton/radioButtonsOrganizer';
import { getEnforcementRetailerCharacteristics } from './enforcementRetailerCharacteristicsTabs';
import * as Styled from './styles';

const { setShadowFilter } = OldFilterEntity.actions;
const { getCorporation } = NewFiltersEntity.actions;
const { fetchAtccYears } = RetailerEntity.actions;
const { getRetailerCorporationOptions } = NewFiltersEntity.selectors;
const { getAtccYears: getAtccYearOptions } = RetailerEntity.selectors;

const { hostname } = window.location;
const IsVaAccount = hostname?.split('.')[0] === 'va';

function VisitsRetailerCharacteristics() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAtccYearsLoading, setIsAtccYearsLoading] = useState(false);
  const corporationOptions = useSelector(getRetailerCorporationOptions);
  const atccYearOptions = useSelector(getAtccYearOptions);
  const {
    filter: { shadowFilters },
  } = useSelector((state) => state);
  const isMdAccount = IsMdAccount();
  const isAzAccount = IsAzAccount();
  const hasResidentialModule = HasAccess(PERMISSIONS.RESIDENTIAL_LOCATION);
  const { pathname } = useLocation();
  const {
    canViewProductsSoldTobacco,
    canViewProductsSoldAlcohol,
    canViewStoreOwnership,
    canViewOtherRetailerDetails,
    canViewAvc,
  } = useHiddenFilters(resolveVisitPage(pathname));
  const haSynar = HasAccess(PERMISSIONS.SYNAR_RETAILER_FIELD);
  const currentYear = new Date().getFullYear().toString();
  const [synarYear, setSynarYear] = useState(currentYear);

  const parseAtccYears = (atccValue) => {
    if (!atccValue || ['t', 'f'].includes(atccValue)) return [];
    return atccValue.split(',').map((y) => ({ label: y, value: y }));
  };

  const defaultAtccYear = useMemo(() => [{ label: currentYear, value: currentYear }], [currentYear]);
  const [atccYearError, setAtccYearError] = useState(false);
  const [atccYears, setAtccYears] = useState(() => {
    const parsed = parseAtccYears(shadowFilters[FILTER_FIELDS.ATCC]);
    return parsed.length > 0 ? parsed : defaultAtccYear;
  });
  const dispatch = useDispatch();
  const isAssesment = pathname?.includes('/assessment');

  useEffect(() => {
    (async function getDataForCorporation() {
      try {
        await dispatch(getCorporation());
      } catch (err) {
        // Non-fatal: loading state cleared in finally; corporation filter shows empty.
      } finally {
        setIsLoading(false);
      }
    })();

    (async function getAtccYearsData() {
      setIsAtccYearsLoading(true);
      try {
        await dispatch(fetchAtccYears());
      } catch (err) {
        // Non-fatal: ATCC years filter shows empty when fetch fails.
      } finally {
        setIsAtccYearsLoading(false);
      }
    })();
  }, [dispatch]);

  const atccFilterValue = shadowFilters[FILTER_FIELDS.ATCC];

  useEffect(() => {
    setAtccYears(parseAtccYears(atccFilterValue));
  }, [atccFilterValue]);

  const tabs = getEnforcementRetailerCharacteristics(
    corporationOptions,
    IsVaAccount,
    hasResidentialModule,
    isMdAccount,
    isAzAccount,
    isAssesment,
    {
      canViewProductsSoldTobacco,
      canViewProductsSoldAlcohol,
      canViewStoreOwnership,
      canViewOtherRetailerDetails,
      canViewAvc,
    },
  );

  const getElement = (item) => {
    switch (item.type) {
      case 'single':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <CheckedList name={item.field} options={item.options} />
          </CollapsedTab>
        );
      case 'multiple':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <Styled.FiltersContainer>
                <Styled.FiltersTitle isSubcategory={elem?.isSubcategory}>{elem.title}</Styled.FiltersTitle>
                <CheckedList name={elem.field} options={elem.options} isSubcategory={elem?.isSubcategory} />
              </Styled.FiltersContainer>
            ))}
          </CollapsedTab>
        );
      case 'double':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <CheckedList name={elem.field} options={elem.options} />
            ))}
          </CollapsedTab>
        );
      case 'proximity':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <ProximityFilterSection />
          </CollapsedTab>
        );
      default:
        return null;
    }
  };

  const filters = {
    [FILTER_FIELDS.ATCC]: null,
  };

  const handleAtccYearChange = (selectedOptions) => {
    const years = selectedOptions || [];
    setAtccYears(years);
    if (years.length > 0) setAtccYearError(false);
    const joinedValue = years.map((o) => o.value).join(',') || null;
    handleChangeFiltersYear(FILTER_FIELDS.ATCC, { value: joinedValue });
  };

  const handleInputChange = (e, fieldName) => {
    const inputValue = e.target.value;

    const numericValue = inputValue.replace(/\D/g, '').slice(0, 4);

    switch (fieldName) {
      case FILTER_FIELDS.SYNAR_YEAR:
        setSynarYear(numericValue);
        break;

      default:
        break;
    }

    if (numericValue.length === 4) {
      handleChangeFiltersYear(fieldName, { value: numericValue });
    }
  };

  const RADIO_BUTTONS = [
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_IS_ATCC,
      checked: shadowFilters[FILTER_FIELDS.ATCC] === 't',
    },
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_ATCC_CERTAIN_YEAR,
      checked: !!(shadowFilters[FILTER_FIELDS.ATCC] && !['t', 'f'].includes(shadowFilters[FILTER_FIELDS.ATCC])),
      customComponent: (
        <Styled.BasedSelectWrapper>
          <BaseSelect
            inputId="atcc-year-select"
            options={atccYearOptions}
            value={atccYears}
            onChange={handleAtccYearChange}
            isMulti
            isLoading={isAtccYearsLoading}
            menuPlacement="bottom"
            error={atccYearError ? 'Please select at least one year' : ''}
          />
        </Styled.BasedSelectWrapper>
      ),
    },
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_NOT_ATCC,
      checked: shadowFilters[FILTER_FIELDS.ATCC] === 'f',
    },
  ];

  const SYNAR_RADIO = [
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_IS_SYNAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === 't',
    },
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_SYNAR_CERTAIN_YEAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === synarYear,
      customComponent: (
        <Styled.TextInputContainer>
          <Styled.TextInputLabel htmlFor="year-input" />
          <Styled.TextInput
            id="year-input"
            type="text"
            value={synarYear}
            onChange={(e) => handleInputChange(e, FILTER_FIELDS.SYNAR_YEAR)}
            placeholder={synarYear}
            maxLength={4}
          />
        </Styled.TextInputContainer>
      ),
    },
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_NOT_SYNAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === 'f',
    },
  ];

  const handleChangeFiltersYear = (field, { value }) => {
    const newFilters = { ...shadowFilters };
    switch (field) {
      case FILTER_FIELDS.ATCC:
      case FILTER_FIELDS.SYNAR_YEAR:
        dispatch(
          setShadowFilter({
            ...newFilters,
            ...{ [field]: value },
          }),
        );
        break;
      default:
        break;
    }
  };

  const handleRadioButton = (value, fieldName, year) => {
    switch (value) {
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_IS_ATCC:
        setAtccYearError(false);
        filters[fieldName] = 't';
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_IS_SYNAR:
        filters[fieldName] = 't';
        break;
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_ATCC_CERTAIN_YEAR:
        if (!year) {
          setAtccYearError(true);
          return;
        }
        setAtccYearError(false);
        filters[fieldName] = year;
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_SYNAR_CERTAIN_YEAR:
        filters[fieldName] = year;
        break;
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_NOT_ATCC:
        setAtccYearError(false);
        filters[fieldName] = 'f';
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_NOT_SYNAR:
        filters[fieldName] = 'f';
        break;
      default:
        break;
    }
    dispatch(setShadowFilter({ ...shadowFilters, ...filters }));
  };

  return (
    <Styled.FilterContainer>
      {isLoading && <Loader />}
      <Styled.Wrapper>
        {tabs.map((item) => getElement(item))}
        <CollapsedTab title="Location" key="152">
          <Location />
        </CollapsedTab>
        {isMdAccount && (
          <FormGroup title="ATCC">
            <RadioButtonsOrganizer
              hideResetButton
              radioButtons={RADIO_BUTTONS}
              onChange={({ value }) =>
                handleRadioButton(value, FILTER_FIELDS.ATCC, atccYears.map((o) => o.value).join(',') || null)
              }
              name="atcc"
              isColumn
            />
          </FormGroup>
        )}
        {haSynar && (
          <FormGroup title="SYNAR">
            <RadioButtonsOrganizer
              hideResetButton
              radioButtons={SYNAR_RADIO}
              onChange={({ value }) => handleRadioButton(value, FILTER_FIELDS.SYNAR_YEAR, synarYear)}
              name="synar_year"
              isColumn
            />
          </FormGroup>
        )}
      </Styled.Wrapper>
    </Styled.FilterContainer>
  );
}

export default VisitsRetailerCharacteristics;
